/**
 * Preloads approval data for SSR.
 */

import type { QueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type {
	ApprovalsContextApprovalsDataQuery$data,
	ApprovalsContextApprovalsDataQuery$variables,
} from './__generated__/ApprovalsContextApprovalsDataQuery.graphql.ts';
import ApprovalsContextApprovalsDataQuery from './__generated__/ApprovalsContextApprovalsDataQuery.graphql.ts';
import { markApprovalsDisabledErrorAsHandled } from './approvalsDisabledError.ts';

export type PreloadApprovalsParams = {
	cloudId: string;
	contentId: string;
	contentStatus: 'CURRENT' | 'DRAFT';
	spaceKey: string;
};

export function preloadApprovals({
	cloudId,
	contentId,
	contentStatus,
	spaceKey,
}: PreloadApprovalsParams): Promise<
	QueryInteropSupportedResult<ApprovalsContextApprovalsDataQuery$data>
> {
	const otherContentStatus = contentStatus === 'CURRENT' ? 'DRAFT' : 'CURRENT';

	return queryInterop<
		ApprovalsContextApprovalsDataQuery$data,
		ApprovalsContextApprovalsDataQuery$variables
	>({
		query: ApprovalsContextApprovalsDataQuery,
		errorPolicy: 'all',
		variables: {
			cloudId,
			contentId,
			contentStatus,
			otherContentStatus,
			transitionType: null,
			spaceKey,
			includeApprovalsBetaFeatures: expValEquals('confluence_approvals_eap', 'isEnabled', true),
			includeApprovalExpirationConfig:
				expValEquals('confluence_approvals_eap', 'isEnabled', true) &&
				expValEquals('confluence_approvals_expiration', 'isEnabled', true),
		},
	}).then((result) => {
		markApprovalsDisabledErrorAsHandled(result.error);

		return result;
	});
}
